import fs from 'node:fs';
import path from 'node:path';
import { yieldForBridgeInteractivePriority, fileBrowserReadInFlight, yieldUntilFileBrowserReadsIdle } from '../../files/browser/in-flight.js';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../../sqlite/cli-database.js';
import { INDEX_WORK_YIELD_EVERY } from './constants.js';

const DEPENDENCY_INSTALL_DIR_NAMES = new Set([
  'node_modules',
  'bower_components',
  'vendor',
  'Pods',
  'Carthage',
  'DerivedData',
  '.yarn',
  '.pnpm-store',
]);

/** Skip dependency trees that should not be indexed for search. Hidden files are searchable. */
export function shouldSkipWorkspaceWalkEntry(name: string): boolean {
  return DEPENDENCY_INSTALL_DIR_NAMES.has(name);
}

export function walkWorkspaceTreeSync(dir: string, baseDir: string, onFile: (relativePath: string) => void): void {
  let names: string[];
  try {
    names = fs.readdirSync(dir);
  } catch {
    return;
  }
  for (const name of names) {
    if (shouldSkipWorkspaceWalkEntry(name)) continue;
    const full = path.join(dir, name);
    let stat: fs.Stats;
    try {
      stat = fs.statSync(full);
    } catch {
      continue;
    }
    const relative = path.relative(baseDir, full).replace(/\\/g, '/');
    if (stat.isDirectory()) {
      walkWorkspaceTreeSync(full, baseDir, onFile);
    } else if (stat.isFile()) {
      onFile(relative);
    }
  }
}

interface WalkYieldState {
  n: number;
}

/**
 * Recursive crawl like {@link walkWorkspaceTreeSync}, yielding every {@link INDEX_WORK_YIELD_EVERY}
 * filesystem entries so the event loop stays responsive during large trees.
 */
export async function walkWorkspaceTreeAsync(
  dir: string,
  baseDir: string,
  onFile: (relativePath: string) => void,
  state: WalkYieldState,
): Promise<void> {
  let names: string[];
  try {
    names = await fs.promises.readdir(dir);
  } catch {
    return;
  }
  for (const name of names) {
    if (shouldSkipWorkspaceWalkEntry(name)) continue;
    if (state.n > 0 && state.n % INDEX_WORK_YIELD_EVERY === 0) {
      if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
      await yieldForBridgeInteractivePriority();
      if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
    }
    if (fileBrowserReadInFlight()) {
      await yieldUntilFileBrowserReadsIdle();
    }
    state.n++;
    const full = path.join(dir, name);
    let stat: fs.Stats;
    try {
      stat = await fs.promises.stat(full);
    } catch {
      continue;
    }
    const relative = path.relative(baseDir, full).replace(/\\/g, '/');
    if (stat.isDirectory()) {
      await walkWorkspaceTreeAsync(full, baseDir, onFile, state);
    } else if (stat.isFile()) {
      onFile(relative);
    }
  }
}

export function createWalkYieldState(): WalkYieldState {
  return { n: 0 };
}
